import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Lock, ShieldCheck, Download, Trash2, Camera, AlertTriangle, Save, ImageOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { Avatar } from "../components/AccountMenu";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const resizeToDataUrl = (file, max = 256) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function AccountSettings() {
  const { user, updateProfile, secureAccount, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "privacy" ? "privacy" : "profile";
  const fileRef = useRef();

  const [name, setName] = useState(user?.display_name || "");
  const [avatar, setAvatar] = useState(user?.avatar_url || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [email, setEmail] = useState(user?.email || "");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);

  // anonymous save-account
  const [aEmail, setAEmail] = useState("");
  const [aPw, setAPw] = useState("");
  const [aName, setAName] = useState("");
  const [savingAnon, setSavingAnon] = useState(false);

  useEffect(() => { document.title = "Account Settings | TaperPath"; return () => { document.title = "TaperPath"; }; }, []);
  useEffect(() => {
    if (user) { setName(user.display_name || ""); setAvatar(user.avatar_url || ""); setEmail(user.email || ""); }
  }, [user]);

  const isAnon = user?.anonymous;
  const isGoogleUser = !isAnon && user?.provider === "google";
  const isEmailUser = !isAnon && !!user?.email && !isGoogleUser;

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    try {
      const dataUrl = await resizeToDataUrl(file);
      setAvatar(dataUrl);
      const updated = await updateProfile({ avatar_url: dataUrl });
      toast.success("Profile picture updated.");
      setAvatar(updated.avatar_url || dataUrl);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const removePhoto = async () => {
    try { await updateProfile({ avatar_url: "" }); setAvatar(""); toast.success("Profile picture removed."); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await updateProfile({ display_name: name }); toast.success("Profile saved."); }
    catch (err) { toast.error(apiErr(err)); }
    setSavingProfile(false);
  };

  const saveCreds = async () => {
    if (!curPw) { toast.error("Enter your current password to confirm changes."); return; }
    const payload = { current_password: curPw };
    if (email && email !== user.email) payload.email = email;
    if (newPw) payload.new_password = newPw;
    if (!payload.email && !payload.new_password) { toast.error("Nothing to change — update your email or password first."); return; }
    setSavingCreds(true);
    try { await updateProfile(payload); toast.success("Login details updated."); setCurPw(""); setNewPw(""); }
    catch (err) { toast.error(apiErr(err)); }
    setSavingCreds(false);
  };

  const saveAnon = async (e) => {
    e.preventDefault();
    setSavingAnon(true);
    try { await secureAccount(aEmail, aPw, aName || undefined); toast.success("Account saved! Your progress is safe and you can log in on any device."); }
    catch (err) { toast.error(apiErr(err)); }
    setSavingAnon(false);
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/auth/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `taperpath-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success("Your data has been downloaded.");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const doDelete = async () => {
    try { await deleteAccount(); toast.success("Your account and data have been deleted."); window.location.replace("/"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Account Settings</h1>
        <p className="mt-2 text-muted-foreground">Manage your profile, login details and your data.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams(v === "privacy" ? { tab: "privacy" } : {})}>
        <TabsList className="rounded-full bg-muted p-1">
          <TabsTrigger value="profile" data-testid="settings-tab-profile" className="rounded-full">Profile</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="settings-tab-privacy" className="rounded-full">Privacy &amp; Data</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6 space-y-6">
          {isAnon && <AnonSaveCard {...{ aName, setAName, aEmail, setAEmail, aPw, setAPw, savingAnon, saveAnon }} />}

          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
            <h2 className="font-head text-lg font-semibold text-[#2C363F]">Profile</h2>
            <div className="mt-5 flex flex-wrap items-center gap-5">
              <Avatar user={{ display_name: name, avatar_url: avatar, picture: user?.picture }} size="h-20 w-20" />
              <div className="flex flex-wrap gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="avatar-file-input" />
                <Button data-testid="avatar-upload-btn" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full"><Camera className="mr-2 h-4 w-4" /> {avatar ? "Change photo" : "Upload photo"}</Button>
                {avatar && <Button data-testid="avatar-remove-btn" variant="ghost" onClick={removePhoto} className="rounded-full text-muted-foreground"><ImageOff className="mr-2 h-4 w-4" /> Remove</Button>}
              </div>
            </div>
            <div className="mt-6">
              <Label>Display name</Label>
              <div className="relative mt-1"><User className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <Input data-testid="settings-display-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl py-6 pl-10" placeholder="A name or nickname" /></div>
            </div>
            <Button data-testid="settings-save-profile" onClick={saveProfile} disabled={savingProfile} className="mt-5 rounded-full bg-[#4A7c59] hover:bg-[#3B6347]"><Save className="mr-2 h-4 w-4" /> {savingProfile ? "Saving…" : "Save profile"}</Button>
          </div>

          {isEmailUser && !isGoogleUser && (
            <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
              <h2 className="font-head text-lg font-semibold text-[#2C363F]">Login &amp; security</h2>
              <p className="mt-1 text-sm text-muted-foreground">To change your email or password, confirm your current password.</p>
              <div className="mt-5 space-y-4">
                <div>
                  <Label>Email address</Label>
                  <div className="relative mt-1"><Mail className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                    <Input data-testid="settings-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl py-6 pl-10" /></div>
                </div>
                <div>
                  <Label>New password</Label>
                  <div className="relative mt-1"><Lock className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                    <Input data-testid="settings-new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Leave blank to keep current" className="rounded-xl py-6 pl-10" /></div>
                </div>
                <div>
                  <Label>Current password <span className="text-[#D96C5B]">*</span></Label>
                  <div className="relative mt-1"><Lock className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                    <Input data-testid="settings-current-password" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="Required to save changes" className="rounded-xl py-6 pl-10" /></div>
                </div>
                <Button data-testid="settings-save-creds" onClick={saveCreds} disabled={savingCreds} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">{savingCreds ? "Saving…" : "Update login details"}</Button>
              </div>
            </div>
          )}

          {isGoogleUser && (
            <div className="rounded-2xl border border-border bg-[#8FB8DE]/10 p-6 text-sm text-[#2C4763]" data-testid="google-managed-note">
              Your login is managed by Google, so your email and password are handled in your Google account. You can still change your display name and photo above.
            </div>
          )}
        </TabsContent>

        <TabsContent value="privacy" className="mt-6 space-y-6">
          {isAnon && (
            <div data-testid="privacy-anon-warning" className="rounded-2xl border border-[#E8907E]/50 bg-[#E8907E]/10 p-6 text-sm text-[#8a4436]">
              <div className="flex items-center gap-2 font-head font-semibold text-[#8a2f22]"><AlertTriangle className="h-5 w-5" /> Your data is not saved permanently</div>
              <p className="mt-2">You're browsing anonymously. Your taper data, progress logs and journal history live only in this session and <strong>will be permanently lost</strong> when it ends. Go to the <button onClick={() => setParams({})} className="underline">Profile tab</button> to create a free account and keep everything safe.</p>
            </div>
          )}

          <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#4A7c59]/10 text-[#4A7c59]"><Download className="h-5 w-5" /></div>
              <div className="flex-1">
                <h2 className="font-head text-lg font-semibold text-[#2C363F]">Download your data</h2>
                <p className="mt-1 text-sm text-muted-foreground">Export everything you've logged — account details, daily logs, journal entries and reminders — as a JSON file.</p>
                <Button data-testid="settings-export-data" onClick={exportData} variant="outline" className="mt-4 rounded-full border-[#4A7c59]/30 text-[#4A7c59]"><Download className="mr-2 h-4 w-4" /> Download my data (JSON)</Button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-[#D96C5B]/40 bg-[#D96C5B]/5 p-6 shadow-sm sm:p-8">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#D96C5B]/10 text-[#D96C5B]"><Trash2 className="h-5 w-5" /></div>
              <div className="flex-1">
                <h2 className="font-head text-lg font-semibold text-[#8a2f22]">Delete your account</h2>
                <p className="mt-1 text-sm text-[#8a4436]">This permanently removes your account and all your data — logs, journal, reminders and buddy connections. This cannot be undone.</p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="settings-delete-account" className="mt-4 rounded-full bg-[#D96C5B] text-white hover:bg-[#c25848]"><Trash2 className="mr-2 h-4 w-4" /> Delete my account</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-head">Delete your account?</AlertDialogTitle>
                      <AlertDialogDescription>This will permanently delete your account and everything you've tracked. This action cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-cancel" className="rounded-full">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="delete-confirm" onClick={doDelete} className="rounded-full bg-[#D96C5B] hover:bg-[#c25848]">Yes, delete everything</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const AnonSaveCard = ({ aName, setAName, aEmail, setAEmail, aPw, setAPw, savingAnon, saveAnon }) => (
  <div data-testid="settings-anon-save" className="rounded-2xl border border-[#4A7c59]/30 bg-[#4A7c59]/[0.07] p-6 shadow-sm sm:p-8">
    <div className="flex items-center gap-2 font-head text-lg font-semibold text-[#2C363F]"><ShieldCheck className="h-5 w-5 text-[#4A7c59]" /> Save my account</div>
    <div className="mt-3 rounded-xl border border-[#E8907E]/50 bg-[#E8907E]/10 p-4 text-sm text-[#8a4436]">
      <strong>Your progress is not permanently saved.</strong> You're browsing anonymously — your taper data, daily logs and journal history exist only in this session and <strong>will be lost</strong> if it ends. Add an email and password below to keep everything safe and log back in on any device. Your data stays private.
    </div>
    <form onSubmit={saveAnon} className="mt-4 space-y-4">
      <div>
        <Label>Display name (optional)</Label>
        <div className="relative mt-1"><User className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="anon-save-name" value={aName} onChange={(e) => setAName(e.target.value)} placeholder="A name or nickname" className="rounded-xl py-6 pl-10" /></div>
      </div>
      <div>
        <Label>Email</Label>
        <div className="relative mt-1"><Mail className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="anon-save-email" type="email" required value={aEmail} onChange={(e) => setAEmail(e.target.value)} placeholder="you@email.com" className="rounded-xl py-6 pl-10" /></div>
      </div>
      <div>
        <Label>Password</Label>
        <div className="relative mt-1"><Lock className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="anon-save-password" type="password" required value={aPw} onChange={(e) => setAPw(e.target.value)} placeholder="At least 6 characters" className="rounded-xl py-6 pl-10" /></div>
      </div>
      <Button data-testid="anon-save-submit" type="submit" disabled={savingAnon} className="w-full rounded-full bg-[#4A7c59] py-6 text-base font-semibold hover:bg-[#3B6347]">{savingAnon ? "Saving…" : "Save my account"}</Button>
    </form>
  </div>
);
